import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Heart, Menu, Search, ShoppingCart, User } from 'lucide-react';
import RetailTemplateOneFooter from '../views/LandingPage/RetailDomain/Template1/components/footer.jsx';
import RetailTemplateOneHeader from '../views/LandingPage/RetailDomain/Template1/components/header.jsx';
import AvikarFooter from '../views/Templates/Avikar-Template/components/footer.jsx';
import AvikarHeader from '../views/Templates/Avikar-Template/components/Header.jsx';
import SunFlareTemplateFooter from '../views/Templates/SunFlare-Theme/components/footer.jsx';
import SunFlareTemplateHeader from '../views/Templates/SunFlare-Theme/components/header.jsx';
import TealFlowTemplateFooter from '../views/Templates/TealFlow-Theme/components/footer.jsx';
import TealFlowTemplateHeader from '../views/Templates/TealFlow-Theme/components/header.jsx';
import {
  getCompanyLogoUrl,
  getCompanyPolicyLinks,
  getTemplateCompanyContext,
  selectTemplateCompanyState
} from './TemplateCompanyUtils.js';
import { getStoredPublishedActiveTemplate } from './TemplateStorage.jsx';
import { getTemplateAccountRoute, isTemplateAuthRoute, useTemplateAuthState } from './TemplateAuthState.jsx';
import TemplateRouterLink from './TemplateRouterLink.jsx';
import { resolveFooterTokens } from './footerTokens.js';
import { linkProps } from './templateLinkConfig.js';
import { resolveHeaderBackground, resolveHeaderTypography, resolveHeaderNavStyle } from './headerTokens.js';
import { useCart } from '../context/CartContext.jsx';

const defaultTheme = {
  primary: '#0891b2',
  secondary: '#232f3e',
  accent: '#B88E2F',
  background: '#f8fafc',
  surface: '#ffffff',
  color: '#111827',
  mutedColor: '#6b7280',
  borderColor: '#e5e7eb',
  fontFamily: 'Inter, Arial, sans-serif',
  headingFontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1280px',
  buttonRadius: '999px',
  headerHeight: '80px',
  footerBackground: '#232f3e'
};

const defaultHeader = {
  visible: true,
  sticky: true,
  height: '80px',
  backgroundColor: '#ffffff',
  borderColor: '#e5e7eb',
  shadow: '0 8px 24px rgba(15, 23, 42, 0.10)',
  logoUrl: '',
  logoContent: null,
  logoAlt: 'Retail Store',
  brandName: 'Retail Store',
  searchPlaceholder: 'Search products, brands, categories',
  cartLabel: 'Cart',
  signInLabel: 'Sign In',
  nav: [
    { label: 'Home', route: '/', visible: true },
    { label: 'Fashion', route: '/category/Fashion', visible: true },
    { label: 'Home Decor', route: '/category/Home Decor', visible: true },
    { label: 'Electronics', route: '/category/Electronics', visible: true },
    { label: 'Beauty', route: '/category/Beauty', visible: true }
  ]
};

const defaultFooter = {
  visible: true,
  backgroundColor: '#232f3e',
  textColor: '#ffffff',
  headingColor: '#ffffff',
  linkColor: '#dbeafe',
  onlineShoppingTitle: 'Online Shopping',
  usefulLinksTitle: 'Useful Links',
  policiesTitle: 'Customer Policies',
  socialTitle: 'Keep In Touch',
  copyrightText: 'All rights reserved.',
  contact: {
    phone: '+1 (678) 855-6990',
    email: 'system@avpstech.com'
  },
  categories: [
    { label: 'Fashion', route: '/category/Fashion', visible: true },
    { label: 'Home Decor', route: '/category/Home Decor', visible: true },
    { label: 'Electronics', route: '/category/Electronics', visible: true },
    { label: 'Beauty', route: '/category/Beauty', visible: true }
  ],
  usefulLinks: [
    { label: 'Blog', route: '#', visible: true },
    { label: 'Careers', route: '#', visible: true },
    { label: 'Site Map', route: '#', visible: true },
    { label: 'Corporate Information', route: '#', visible: true }
  ],
  legalLinks: [
    { label: 'Terms Of Use', route: '/termsConditions', visible: true },
    { label: 'Privacy Policy', route: '/privacyPolicy', visible: true },
    { label: 'User Acceptance', route: '/userAcceptance', visible: true },
    { label: 'Cookies', route: '/cookies', visible: true },
    { label: 'FAQ', route: '#', visible: true }
  ],
  socialLinks: [
    { label: 'Facebook', route: '#', visible: true },
    { label: 'Instagram', route: '#', visible: true },
    { label: 'LinkedIn', route: '#', visible: true }
  ]
};

const assetUrl = (asset, fallback = '') => {
  if (!asset) return fallback || '';
  if (typeof asset === 'string') return asset;
  return asset.displayLink || asset.storedLink || fallback || '';
};

const visibleItems = (items = []) => (Array.isArray(items) ? items.filter((item) => item?.visible !== false) : []);

const resolveHeaderPositionMode = (header = {}) => {
  if (['normal', 'sticky', 'fixed', 'overlay'].includes(header.positionMode)) return header.positionMode;
  if (header.overlay === true || header.headerTheme === 'transparent') return 'overlay';
  return header.sticky === false ? 'normal' : 'sticky';
};

const isTransparentHeader = (header = {}) => (
  header.headerTheme === 'transparent'
  || header.backgroundColor === 'transparent'
  || header.transparent === true
  || resolveHeaderPositionMode(header) === 'overlay'
);

const resolveActiveRetailTemplate = () => (
  getStoredPublishedActiveTemplate({
    companyId: typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : '',
    domainType: 'RETAIL'
  })
);

const useActiveRetailTemplate = () => {
  const [template, setTemplate] = useState(resolveActiveRetailTemplate);

  useEffect(() => {
    const refresh = () => setTemplate(resolveActiveRetailTemplate());
    window.addEventListener('storage', refresh);
    window.addEventListener('avps-template-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('avps-template-updated', refresh);
    };
  }, []);

  return template;
};

const themeOf = (template) => ({ ...defaultTheme, ...(template?.attributes?.theme || {}) });

const legacyRetailTemplateOf = (template) => (
  template?.baseTemplateKey
  || template?.attributes?.sourceTemplate
  || 'SecondRetailTemplate'
);

const legacyRetailHeaderOf = (template) => {
  switch (legacyRetailTemplateOf(template)) {
    case 'TealFlowTemplate': return TealFlowTemplateHeader;
    case 'SunFlareTemplate': return SunFlareTemplateHeader;
    case 'AvikarTemplate': return AvikarHeader;
    default: return RetailTemplateOneHeader;
  }
};

const legacyRetailFooterOf = (template) => {
  switch (legacyRetailTemplateOf(template)) {
    case 'TealFlowTemplate': return TealFlowTemplateFooter;
    case 'SunFlareTemplate': return SunFlareTemplateFooter;
    case 'AvikarTemplate': return AvikarFooter;
    default: return RetailTemplateOneFooter;
  }
};

const legacyRetailHeader = (template) => {
  const LegacyHeader = legacyRetailHeaderOf(template);
  return <LegacyHeader />;
};

export const ConfigurableRetailHeader = ({ template: providedTemplate, fallback = true }) => {
  const navigate = useNavigate();
  const auth = useTemplateAuthState();
  const { total_items: cartItemCount = 0 } = useCart();
  const companyState = useSelector(selectTemplateCompanyState);
  const storedTemplate = useActiveRetailTemplate();
  const template = providedTemplate || storedTemplate;

  if (!template && fallback) return <RetailTemplateOneHeader />;
  if (!template && !fallback) return null;

  const theme = themeOf(template);
  const header = { ...defaultHeader, ...(template?.attributes?.header || {}) };
  if (header.visible === false) return null;
  if (header.mode === 'legacy' || (template?.attributes?.runtime?.renderMode === 'legacy-parity' && header.mode !== 'custom')) {
    return fallback ? legacyRetailHeader(template) : null;
  }

  const companyContext = getTemplateCompanyContext(template?.company, companyState);
  const logo = getCompanyLogoUrl(companyContext) || assetUrl(header.logoContent, header.logoUrl);
  const nav = visibleItems(header.nav).filter((item) => !auth.isLoggedIn || !isTemplateAuthRoute(item));
  const accountRoute = getTemplateAccountRoute('RETAIL');
  const positionMode = resolveHeaderPositionMode(header);
  const overlayHeader = positionMode === 'overlay';
  const fixedHeader = positionMode === 'fixed';
  const transparentHeader = isTransparentHeader(header);
  const headerHeight = header.height || theme.headerHeight;
  // Task 8.3: resolved header customization (additive — unset reproduces today's look).
  const headerBackgroundTokens = resolveHeaderBackground(theme, header || {});
  const headerTypography = resolveHeaderTypography(theme, header || {});
  const headerNavStyle = resolveHeaderNavStyle(theme, header || {});
  const headerBackgroundOverride = headerBackgroundTokens.mode === 'solid'
    && header && header.background && (header.background.color || header.background.mode === 'solid')
    ? headerBackgroundTokens.backgroundColor
    : null;
  const navSpacing = headerNavStyle.spacing != null ? headerNavStyle.spacing : 18;
  const headerBackground = transparentHeader
    ? (header.backgroundGradient || 'transparent')
    : (headerBackgroundOverride || header.backgroundGradient || header.backgroundColor || '#ffffff');
  const handleSearch = (event) => {
    event.preventDefault();
    const search = new FormData(event.currentTarget).get('search');
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    navigate(`/product${params}`);
  };

  return (
    <>
    <header
      id="retail-template-header"
      style={{
        background: headerBackground,
        borderBottom: `${header.borderWidth || '1px'} solid ${transparentHeader ? (header.borderColor || 'transparent') : (header.borderColor || theme.borderColor)}`,
        boxShadow: transparentHeader ? (header.shadow || 'none') : (header.shadow || 'none'),
        position: fixedHeader ? 'fixed' : overlayHeader ? 'absolute' : positionMode === 'normal' ? 'relative' : 'sticky',
        top: positionMode === 'normal' ? 'auto' : 'env(safe-area-inset-top, 0px)',
        left: fixedHeader || overlayHeader ? 0 : undefined,
        right: fixedHeader || overlayHeader ? 0 : undefined,
        width: fixedHeader || overlayHeader ? '100%' : undefined,
        zIndex: 9999,
        fontFamily: theme.fontFamily
      }}
    >
      <div className="retail-template-header-inner" style={{ maxWidth: theme.pageWidth, minHeight: headerHeight, margin: '0 auto', padding: '0 18px', display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignItems: 'center', gap: 18 }}>
        <TemplateRouterLink to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, color: headerTypography.color || theme.secondary, fontFamily: headerTypography.fontFamily, textDecoration: 'none' }}>
          <Menu className="retail-template-mobile-menu" size={22} />
          {logo ? <img src={logo} alt={header.logoAlt || 'Retail Store'} style={{ maxHeight: 48, maxWidth: 150, objectFit: 'contain' }} /> : <span style={{ fontSize: headerTypography.fontSize || 22, fontWeight: headerTypography.fontWeight || 900, letterSpacing: headerTypography.letterSpacing }}>{header.brandName || 'Retail Store'}</span>}
        </TemplateRouterLink>
        <form onSubmit={handleSearch} className="retail-template-search" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', border: `1px solid ${theme.borderColor}`, borderRadius: 999, overflow: 'hidden', background: '#fff' }}>
          <input name="search" placeholder={header.searchPlaceholder || 'Search products'} style={{ border: 0, outline: 0, padding: '12px 16px', fontSize: 14, minWidth: 0 }} />
          <button type="submit" style={{ border: 0, background: theme.primary, color: '#fff', padding: '0 16px', display: 'grid', placeItems: 'center' }}><Search size={18} /></button>
        </form>
        <div className="retail-template-actions" style={{ display: 'flex', alignItems: 'center', gap: 14, color: theme.secondary }}>
          <TemplateRouterLink to={auth.isLoggedIn ? accountRoute : '/sign-in'} aria-label={auth.isLoggedIn ? 'My Profile' : (header.signInLabel || 'Sign In')} title={auth.isLoggedIn ? auth.displayName : (header.signInLabel || 'Sign In')} style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
            {auth.isLoggedIn && auth.avatarUrl ? (
              <img src={auth.avatarUrl} alt="" style={{ width: 24, height: 24, borderRadius: 999, objectFit: 'cover' }} />
            ) : (
              <User size={22} />
            )}
          </TemplateRouterLink>
          <TemplateRouterLink to="/profile/wishlist" aria-label="Wishlist" style={{ color: 'inherit' }}><Heart size={22} /></TemplateRouterLink>
          <TemplateRouterLink to="/cart" aria-label={header.cartLabel || 'Cart'} style={{ color: 'inherit', position: 'relative', display: 'inline-flex' }}><ShoppingCart size={23} /><span style={{ position: 'absolute', top: -8, right: -10, background: theme.accent, color: '#fff', borderRadius: 999, fontSize: 10, minWidth: 16, height: 16, display: 'grid', placeItems: 'center' }}>{cartItemCount}</span></TemplateRouterLink>
        </div>
      </div>
      <nav className="retail-template-nav" style={{ maxWidth: theme.pageWidth, margin: '0 auto', padding: '0 18px 12px', display: 'flex', gap: navSpacing }}>
        {nav.map((item) => <TemplateRouterLink key={`${item.label}-${item.route}`} {...linkProps(item.link, item.route)} style={{ color: headerNavStyle.linkColor || theme.color, fontFamily: headerNavStyle.fontFamily, textDecoration: 'none', fontWeight: headerNavStyle.fontWeight != null ? headerNavStyle.fontWeight : 700, fontSize: headerNavStyle.fontSize != null ? headerNavStyle.fontSize : 14 }}>{item.label}</TemplateRouterLink>)}
      </nav>
      <style>{`
        @media (min-width: 901px) {
          .retail-template-mobile-menu { display: none !important; }
        }
        @media (max-width: 900px) {
          .retail-template-search,
          .retail-template-actions,
          .retail-template-nav {
            display: none !important;
          }
          .retail-template-header-inner {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
    </header>
    {fixedHeader && !transparentHeader && <div aria-hidden="true" style={{ height: headerHeight }} />}
    </>
  );
};

export const ConfigurableRetailFooter = ({ template: providedTemplate, fallback = true, company }) => {
  const companyState = useSelector(selectTemplateCompanyState);
  const storedTemplate = useActiveRetailTemplate();
  const template = providedTemplate || storedTemplate;

  if (!template && fallback) return <RetailTemplateOneFooter company={company} />;
  if (!template && !fallback) return null;

  const theme = themeOf(template);
  const footer = { ...defaultFooter, ...(template?.attributes?.footer || {}) };
  if (footer.visible === false) return null;
  if (footer.mode === 'legacy' || (template?.attributes?.runtime?.renderMode === 'legacy-parity' && footer.mode !== 'custom')) {
    const LegacyFooter = legacyRetailFooterOf(template);
    return fallback ? <LegacyFooter company={company} /> : null;
  }

  const companyContext = getTemplateCompanyContext(template?.company, companyState, company);
  const legalLinks = getCompanyPolicyLinks(companyContext);
  const tokens = resolveFooterTokens(theme, template?.attributes?.footer);
  const headingColor = tokens.headingColor || '#ffffff';
  const linkColor = tokens.linkColor || '#dbeafe';
  // Task 9.3: additive footer customization (typography / columns / editable links).
  // An UNSET field keeps today's look (non-regression).
  const footerSource = template?.attributes?.footer || {};
  const footerTypography = footerSource.typography && typeof footerSource.typography === 'object' ? footerSource.typography : {};
  const footerTypographyStyle = {
    ...(footerTypography.textAlign ? { textAlign: footerTypography.textAlign } : {}),
    ...(footerTypography.fontSize != null && footerTypography.fontSize !== '' ? { fontSize: footerTypography.fontSize } : {}),
    ...(footerTypography.fontWeight != null && footerTypography.fontWeight !== '' ? { fontWeight: footerTypography.fontWeight } : {}),
    ...(footerTypography.letterSpacing != null && footerTypography.letterSpacing !== '' ? { letterSpacing: footerTypography.letterSpacing } : {})
  };
  const editableFooterLinks = Array.isArray(footerSource.links)
    ? footerSource.links.filter((item) => item && item.visible !== false)
    : [];
  const footerColumnsOverride = Number(footerSource.columns);
  const groups = [
    { title: footer.onlineShoppingTitle || 'Online Shopping', items: visibleItems(footer.categories) },
    { title: footer.usefulLinksTitle || 'Useful Links', items: visibleItems(footer.usefulLinks) },
    ...(legalLinks.length ? [{ title: footer.policiesTitle || 'Customer Policies', items: legalLinks }] : []),
    { title: footer.socialTitle || 'Keep In Touch', items: visibleItems(footer.socialLinks), contact: { ...defaultFooter.contact, ...(footer.contact || {}) } }
  ];
  const footerColumnCount = Number.isFinite(footerColumnsOverride) && footerColumnsOverride > 0
    ? footerColumnsOverride
    : Math.min(4, groups.length);

  return (
    <footer id="retail-template-footer" style={{ background: tokens.backgroundColor, color: tokens.textColor, fontFamily: tokens.fontFamily, ...(footerSource.minHeight ? { minHeight: footerSource.minHeight } : {}), ...footerTypographyStyle }}>
      <div id="retail-template-footer-grid" style={{ maxWidth: theme.pageWidth, margin: '0 auto', padding: '64px 24px', display: 'grid', gridTemplateColumns: `repeat(${footerColumnCount}, minmax(0, 1fr))`, gap: 28 }}>
        {groups.map((group) => (
          <div key={group.title}>
            <h3 style={{ margin: '0 0 18px', color: headingColor, fontSize: 18 }}>{group.title}</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              {group.items.map((item) => <TemplateRouterLink key={`${item.label}-${item.route}`} to={item.route || '#'} style={{ color: linkColor, textDecoration: 'none', fontSize: 14 }}>{item.label}</TemplateRouterLink>)}
              {group.contact && <><span>{group.contact.phone}</span><span>{group.contact.email}</span></>}
            </div>
          </div>
        ))}
        {editableFooterLinks.length > 0 && (
          <div>
            <h3 style={{ margin: '0 0 18px', color: headingColor, fontSize: 18 }}>{footer.linksTitle || 'Links'}</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              {editableFooterLinks.map((item, index) => (
                <TemplateRouterLink key={`${item.label || 'link'}-${index}`} {...linkProps(item.link, item.route)} style={{ color: linkColor, textDecoration: 'none', fontSize: 14 }}>{item.label}</TemplateRouterLink>
              ))}
            </div>
          </div>
        )}
      </div>
      <div style={{ maxWidth: theme.pageWidth, margin: '0 auto', padding: '0 24px 28px', color: tokens.textColor, fontSize: 13 }}>
        (c) {new Date().getFullYear()} {template?.company?.name || 'Retail Store'}. {footer.copyrightText || 'All rights reserved.'}
      </div>
      <style>{`
        @media (max-width: 900px) {
          #retail-template-footer-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }
      `}</style>
    </footer>
  );
};
